import React, { useState, useEffect } from 'react';
import { useNotebook } from '../hooks/useNotebook';
import FolderSidebar from '../components/Notebook/FolderSidebar';
import PageList from '../components/Notebook/PageList';
import PageEditor from '../components/Notebook/PageEditor';

export interface NotebookFolder {
  id: string;
  name: string;
  description?: string;
  color: string;
  pageCount: number;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface NotebookPage {
  id: string;
  title: string;
  content: string;
  contentType: string;
  linkedClients: string[];
  linkedTranscripts: string[];
  linkedTemplates: string[];
  linkedPages: string[];
  tags: string[];
  isPinned: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

const NotebookPage: React.FC = () => {
  const {
    folders,
    pages,
    selectedFolder,
    selectedPage,
    loading,
    error,
    createFolder,
    createPage,
    updatePage,
    updateFolder,
    deleteFolder,
    deletePage,
    selectFolder,
    selectPage,
    loadFolders,
    loadPages
  } = useNotebook();

  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  useEffect(() => {
    loadFolders();
  }, []);

  useEffect(() => {
    if (selectedFolder) {
      loadPages(selectedFolder.id);
    }
  }, [selectedFolder]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    try {
      await createFolder({
        name: newFolderName.trim(),
        description: '',
        color: '#6366f1'
      });
      setNewFolderName('');
      setIsCreatingFolder(false);
    } catch (error) {
      console.error('Failed to create folder:', error);
    }
  };

  const handleCreatePage = async (folderId: string) => {
    try {
      const newPage = await createPage({
        folderId,
        title: 'Untitled Note',
        content: '',
        contentType: 'html'
      });
      selectPage(newPage);
    } catch (error) {
      console.error('Failed to create page:', error);
    }
  };

  const handleSavePage = async (pageData: Partial<NotebookPage>) => {
    if (!selectedPage) return;
    
    try {
      await updatePage({
        ...selectedPage,
        ...pageData
      });
    } catch (error) {
      console.error('Failed to save page:', error);
    }
  };

  if (loading && folders.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Left Column - Folders */}
      <div className="w-80 flex-shrink-0">
        <FolderSidebar
          folders={folders}
          selectedFolder={selectedFolder}
          onSelectFolder={selectFolder}
          onCreatePage={handleCreatePage}
          isCreatingFolder={isCreatingFolder}
          newFolderName={newFolderName}
          onNewFolderNameChange={setNewFolderName}
          onCreateFolder={handleCreateFolder}
          onStartCreatingFolder={() => setIsCreatingFolder(true)}
          onCancelCreatingFolder={() => {
            setIsCreatingFolder(false);
            setNewFolderName('');
          }}
        />
      </div>

      {/* Middle Column - Page List */}
      <div className="w-80 flex-shrink-0 border-r border-gray-200">
        <PageList
          pages={pages}
          selectedPage={selectedPage}
          selectedFolder={selectedFolder}
          onSelectPage={selectPage}
          onCreatePage={handleCreatePage}
          onDeletePage={deletePage}
          onUpdateFolder={updateFolder}
          onDeleteFolder={deleteFolder}
          loading={loading}
        />
      </div>

      {/* Right Column - Page Editor */}
      <div className="flex-1">
        <PageEditor
          page={selectedPage}
          onSave={handleSavePage}
        />
      </div>
    </div>
  );
};

export default NotebookPage; 